import {
  AlertTooltip,
  Button,
  IconLoadingSpinner,
  Typography,
  bodySmallTypography,
  pxToRem,
} from '@leaftrade/leaftrade-component-library';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useStorefrontState } from '../../../hooks/useReduxState';
import {
  getCartData,
  setLocationFromCart,
  toggleQuickOrderClearModal,
} from '../../../store/actions/storefrontActions';
import { hasQuickOrderPendingQuantityEdits } from './quickOrderTableUtils';
import { QuickOrderFooterActionsWrapper } from './QuickOrderTable.styles';
import QuickOrderTableTotals from './QuickOrderTableTotals';

const QUICK_ORDER_DISCLAIMER =
  "Submitted order quantities and pricing are subject to the seller's packaged inventory availability and are not always guaranteed. You will be notified if the vendor makes any changes to your order. Surcharges and cart-based discounts are not represented in the Quick Order grid. They can be reviewed at an individual cart level.";

const QuickOrderTableFooterActions = ({ totalItems, totalPrice }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart, quickOrderData, shopper, vendor } = useStorefrontState();
  const { multiCartSummary } = cart;
  const { items, pendingQuantityEdits } = quickOrderData;
  const { currentLocation, dispensary } = shopper;
  const { slug } = vendor;

  const [isProcessingReview, setIsProcessingReview] = useState(false);
  const [reviewTooltipOpen, setReviewTooltipOpen] = useState(false);

  const hasStagedItems = items?.length > 0;
  const hasItemsWithQuantity = totalItems > 0;
  const isUpdatingQuantity =
    hasQuickOrderPendingQuantityEdits(pendingQuantityEdits);

  const handleReviewAllCartsClick = async () => {
    setIsProcessingReview(true);

    if (
      !multiCartSummary.carts.find(
        (cartSummary) =>
          Number(cartSummary.locationId) === Number(currentLocation.id)
      )
    ) {
      await dispatch(
        setLocationFromCart({
          dispensaryId: dispensary.id,
          locationId: multiCartSummary.carts[0].locationId,
          vendorSlug: slug,
        })
      );
    }

    await dispatch(
      getCartData({
        slug,
        token: multiCartSummary.carts[0].token,
      })
    );
    await navigate(`/storefront/sellers/${slug}/cart/`);
    setIsProcessingReview(false);
  };

  const reviewAllCartsEndIcon = (
    <AlertTooltip
      alertType='info'
      id='quick-order-review-all-carts-disclaimer'
      isOpen={reviewTooltipOpen}
      setIsOpen={setReviewTooltipOpen}
      positionStrategy='fixed'
      tooltipPlacement='top'
    >
      <Typography bodyStyle={bodySmallTypography}>
        {QUICK_ORDER_DISCLAIMER}
      </Typography>
    </AlertTooltip>
  );

  return (
    <QuickOrderFooterActionsWrapper data-testid='quick-order-table-footer-actions--component--div'>
      {hasItemsWithQuantity && (
        <Button
          disabled={isProcessingReview || isUpdatingQuantity}
          endIcon={!isProcessingReview ? reviewAllCartsEndIcon : null}
          onClick={handleReviewAllCartsClick}
          sx={{ width: pxToRem(180) }}
          variant='primary'
        >
          {isProcessingReview ? (
            <IconLoadingSpinner />
          ) : isUpdatingQuantity ? (
            'Updating Carts...'
          ) : (
            'Review All Carts'
          )}
        </Button>
      )}
      {hasStagedItems && (
        <Button
          onClick={() => dispatch(toggleQuickOrderClearModal({ isOpen: true }))}
          variant='destructive'
        >
          Clear All
        </Button>
      )}
      <QuickOrderTableTotals totalItems={totalItems} totalPrice={totalPrice} />
    </QuickOrderFooterActionsWrapper>
  );
};

QuickOrderTableFooterActions.propTypes = {
  totalItems: PropTypes.number.isRequired,
  totalPrice: PropTypes.number.isRequired,
};

export default QuickOrderTableFooterActions;
